import {DOCUMENT_FIELDS_PATH} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER, entityFieldsMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {issuePath} from '@github-ui/paths'
import {useMutation} from '@github-ui/react-query'
import {updateIssueFieldValueMutation as updateIssueFieldValueQueryId} from '@github-ui/server-persisted-queries'
import type {UpdateIssueFieldValueMutationMutation} from '@github-ui/server-persisted-queries/types/updateIssueFieldValueMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

import {executeNativeCreateIssueFieldValueMutation} from './create-issue-field-value-mutation'
import {
  type IssueFieldValueMutationInput,
  normalizeIssueFieldValue,
  type OptimisticIssueFieldValue,
  upsertIssueFieldValue,
} from './helpers'

const UPDATE_ERROR = 'Update issue field value failed'
let fieldValueMutationOperationSequence = 0

function nextIssueFieldValueOperationId() {
  return `issue-field-value-${fieldValueMutationOperationSequence++}`
}

type IssueFieldValueMutationInputWithScope = IssueFieldValueMutationInput & {
  fieldValueExists: boolean
}
type IssueFieldValueMutationOptions = {
  input: IssueFieldValueMutationInputWithScope
  optimisticFieldValue?: OptimisticIssueFieldValue
  onCompleted?: () => void
  onError?: (error?: Error) => void
}
type IssueFieldValueMutationRequest = {
  input: IssueFieldValueMutationInput
  fieldValueExists: boolean
  optimisticFieldValue: OptimisticIssueFieldValue
}
type IssueFieldValueMutationVariables = {
  operation: ReturnType<typeof upsertIssueFieldValue>
  operationId: string
  request: IssueFieldValueMutationRequest
}
type ConfirmedFieldValue = NonNullable<
  NonNullable<UpdateIssueFieldValueMutationMutation['updateIssueFieldValue']>['issueFieldValue']
>
type UpdateResponse = {data?: UpdateIssueFieldValueMutationMutation | null; errors?: readonly unknown[] | null}

async function executeNativeUpdateIssueFieldValueMutation(
  owner: string,
  repo: string,
  number: number,
  input: IssueFieldValueMutationInput,
): Promise<ConfirmedFieldValue> {
  const query = new URLSearchParams({queryId: updateIssueFieldValueQueryId})
  const path = `${issuePath({owner, repo, issueNumber: number})}/field_value.json`
  const response = await reactFetchJSON(`${path}?${query.toString()}`, {
    method: 'PATCH',
    body: {issueField: input.issueField},
  })
  if (!response.ok) throw new Error(`${UPDATE_ERROR} (${response.status})`)

  let json: UpdateResponse
  try {
    json = (await response.json()) as UpdateResponse
  } catch {
    throw new Error(`${UPDATE_ERROR}: response was invalid`)
  }
  const message = json.errors?.length
    ? json.errors
        .flatMap(error => {
          return error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
            ? [error.message]
            : []
        })
        .join(', ')
    : undefined
  if (!json.data) throw new Error(message || `${UPDATE_ERROR}: response data was missing`)

  const payload = json.data.updateIssueFieldValue
  if (!payload) throw new Error(message || `${UPDATE_ERROR}: payload was missing`)
  if (!payload.issue) throw new Error(message || `${UPDATE_ERROR}: issue was missing`)
  if (payload.issue.id !== input.issueId) throw new Error(message || `${UPDATE_ERROR}: issue did not match`)
  if (!payload.issueFieldValue) throw new Error(message || `${UPDATE_ERROR}: issue field value was missing`)

  return payload.issueFieldValue
}

async function issueFieldMutationRequest(
  owner: string,
  repo: string,
  number: number,
  request: IssueFieldValueMutationRequest,
): Promise<ConfirmedFieldValue> {
  if (request.fieldValueExists) {
    return executeNativeUpdateIssueFieldValueMutation(owner, repo, number, request.input)
  }
  return executeNativeCreateIssueFieldValueMutation(owner, repo, number, request.input)
}

async function settleIssueFieldValueUpdate(
  issueFieldValue: ConfirmedFieldValue,
  request: IssueFieldValueMutationRequest,
  operationId: string,
) {
  const confirmed = normalizeIssueFieldValue(issueFieldValue, request.optimisticFieldValue)
  if (!confirmed || issueFieldValue.__typename !== request.optimisticFieldValue.__typename) {
    throw new Error(`${UPDATE_ERROR}: issue field value was invalid`)
  }
  await notifySettlement(
    upsertIssueFieldValue(request.input.issueId, confirmed),
    ENTITY_FIELDS_OWNER,
    DOCUMENT_FIELDS_PATH,
    operationId,
  )
}

export function useIssueFieldValueMutation(owner: string, repo: string, number: number) {
  const {mutateAsync} = useMutation<ConfirmedFieldValue, Error, IssueFieldValueMutationVariables>({
    mutationKey: entityFieldsMutationKey(),
    mutationFn: ({request}) => issueFieldMutationRequest(owner, repo, number, request),
    onSuccess: (issueFieldValue, {request, operationId}) =>
      settleIssueFieldValueUpdate(issueFieldValue, request, operationId),
  })

  return useCallback(
    (options: IssueFieldValueMutationOptions) => {
      if (!options.optimisticFieldValue) {
        options.onError?.(new Error(`${UPDATE_ERROR}: optimistic field value was missing`))
        return
      }
      const operationId = nextIssueFieldValueOperationId()
      const request: IssueFieldValueMutationRequest = {
        input: {issueId: options.input.issueId, issueField: options.input.issueField},
        fieldValueExists: options.input.fieldValueExists,
        optimisticFieldValue: options.optimisticFieldValue,
      }
      const operation = upsertIssueFieldValue(options.input.issueId, options.optimisticFieldValue)
      void (async () => {
        try {
          await mutateAsync({operation, operationId, request})
          options.onCompleted?.()
        } catch (error) {
          options.onError?.(error instanceof Error ? error : new Error(UPDATE_ERROR))
        }
      })()
    },
    [mutateAsync],
  )
}
